import {AfterViewInit, ChangeDetectorRef, Component, computed, inject, OnDestroy, TemplateRef, ViewChild} from '@angular/core';
import {Button, ButtonDirective} from 'primeng/button';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {FormBuilder, IMsfFormBuilder} from '@components/form-builder/form-builder';
import {
  FactSeasonDto,
  Field,
  FieldService,
  RentalTermsRelation,
  RentalTermsRelationService,
  Season,
  SeasonService,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {ConfirmationService} from 'primeng/api';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {CROP_COLOR_MAP} from '@app/utils/_crop-colors';
import {FieldCropsColorPipe} from '@app/utils/pipes/field-crops-color-pipe';
import {ServerTranslateService} from '@services/server-translate.service';
import {OldMapComponent} from '@components/old-map/old-map.component';
import {extractCoordinates, olCalculateAreaFromPolygon} from '@app/utils/polygons.utils';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {FilterOperator} from '@app/utils/filter-operator';
import {DatePipe} from '@angular/common';
import {SiblingPolygon} from '@components/old-map/managers';
import {takeUntil, switchMap, map, catchError} from 'rxjs/operators';
import {of, Subject, throwError, Observable} from 'rxjs';
import {PlantingMethodService} from '@6grain/masofa-monolith-api-client/dictionaries/api/plantingMethod.service';
import {
  Crop,
  ManualSurveyBlockReasonService,
  RecommendedPlantingDatesService,
  RemoteSurveyBlockReasonService,
  Region,
  RentalTerm,
  RentalTermsService, SeedVarietyService,
} from '@6grain/masofa-monolith-api-client/dictionaries';
import {TableColumn} from '@components/data-table/data-table.component';
import {
  SeasonPeriodEdit
} from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-edit-season-map/season-period-edit/season-period-edit';
import {
  applySeasonPlanLockedSnapshot,
  isSeasonPlanDataLocked,
  isSeasonPlanned,
  resolvePlanDataSourceLabelKey,
  snapshotSeasonPlanLockedFields,
} from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-edit-season-map/season-plan-data-lock.util';
import {buildCreatePureFactSeasonPayload, buildSeasonUpdatePayload} from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-edit-season-map/season-update-payload.util';
import {FormsModule} from '@angular/forms';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {PassportManagerService} from '@app/pages/electronic-field-map/pages/fields/passport/passport-manager.service';
import {Loading} from '@components/ui/loading/loading';
import {Divider} from 'primeng/divider';
import {CropYieldForecastService} from '@6grain/masofa-monolith-api-client/ai-inference';
import {CropYieldForecast} from '@6grain/masofa-monolith-api-client/ai-inference/model/cropYieldForecast';
import {TabItem, TabsDynamic} from '@components/ui/tabs/tabs-dynamic';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {IFieldSources} from '@models/IFieldSource';
import {PassportMenuGeneralHistoryComponent} from '@app/pages/electronic-field-map/pages/fields/passport/menu/passport-menu-general-history/passport-menu-general-history.component';

declare var ol: any;

@Component({
  selector: 'app-modal-form-with-map',
  standalone: true,
  templateUrl: './modal-edit-season-map.html',
  imports: [
    Button,
    FormBuilder,
    ButtonDirective,
    TranslatePipe,
    ConfirmDialogModule,
    OldMapComponent,
    Loading,
    SeasonPeriodEdit,
    FormsModule,
    DateFormatLangPipe,
    Divider,
    TabsDynamic,
    PassportMenuGeneralHistoryComponent,
  ],
  providers: [ConfirmationService, DatePipe, FieldCropsColorPipe],
  styleUrl: './modal-edit-season-map.scss',
})
export class ModalEditSeasonMap implements AfterViewInit, OnDestroy {

  @ViewChild('infosTemplate') infosTemplate!: TemplateRef<any>;
  @ViewChild('sourceTemplate') sourceTemplate!: TemplateRef<any>;
  @ViewChild('contractTemplate') contractTemplate!: TemplateRef<any>;
  @ViewChild('historyTemplate') historyTemplate!: TemplateRef<any>;

  protected readonly CROP_COLOR_MAP = CROP_COLOR_MAP;
  $destroy = new Subject();
  formData: Season | any;
  /** Planned sowing (IsPlanned) — view-only, no save. */
  isPlannedSowing = false;
  private planFieldsSnapshot: Record<string, unknown> | null = null;
  fieldArea: number = 0;
  isSaving: boolean = false;
  canSave: boolean = true;
  private mainFormCanSave: boolean = true;
  private seasonPeriodCanSave: boolean = true;
  private mapCanSave: boolean = true;
  center: [any?, any?] = [];
  isLoadingMap: boolean = true;
  crops: Crop[] = [];
  activeSeasonsPolygonJson: SiblingPolygon[] = [];
  private activeSeasons: Season[] = [];
  datePipe = inject(DatePipe);
  private fieldCropsColorPipe = inject(FieldCropsColorPipe);
  private serverTranslate = inject(ServerTranslateService);
  parentField!: Field;
  tableColumns: TableColumn[] = [];
  currentRentalTermRelation: RentalTerm | null = null;
  mainFormDateError: string | null = null;
  private lastCropId: any = null;
  private region!: Region;
  private cdr = inject(ChangeDetectorRef);
  private disableSiblingPolygons = false;
  /** Used to reload sibling polygons when user changes planting/harvesting period inside the modal. */
  private lastActiveSeasonsPeriodKey: string | null = null;

  fieldData: Field;
  tabs: TabItem[] = [];


  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private seasonService: SeasonService,
    private seedVarietyService: SeedVarietyService,
    private fieldService: FieldService,
    private plantingMethodService: PlantingMethodService,
    private cropYieldForecastService: CropYieldForecastService,
    public translateService: TranslateService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private rentalTermsService: RentalTermsService,
    private rentalTermsRelation: RentalTermsRelationService,
    private passportManager: PassportManagerService,
    private regionManager: RegionManagerService,
    private manualSurveyBlockReasonService: ManualSurveyBlockReasonService,
    private remoteSurveyBlockReasonService: RemoteSurveyBlockReasonService,
    private recommendedPlantingDatesService: RecommendedPlantingDatesService,
  ) {
    this.disableSiblingPolygons = this.config.data?.disableSiblingPolygons === true;
    this.passportManager.initialize(this.datePipe);
    this.formData = config.data.model;
    this.isPlannedSowing = isSeasonPlanned(this.formData);
    if (!this.isPlannedSowing && isSeasonPlanDataLocked(this.formData)) {
      this.planFieldsSnapshot = snapshotSeasonPlanLockedFields(this.formData);
    }
    this.fieldData = config.data.fieldData;
    config.data['fields'] = [];
    config.data.fields = [];
    this.loadField();
    this.loadCrops();
    this.loadRentalTermsRelation();
    this.loadScheduledDates();
    if (!this.disableSiblingPolygons) {
      this.loadActiveSeason();
    }


    if (this.formData.id) {
      this.loadCropYieldForecast();
    }
  }


  prepareSiblingsPolygons(): SiblingPolygon[] {
    return this.activeSeasons
      .filter(s => s.id !== this.formData.id && s.polygonJson)
      .map(s => {
        const crop = this.crops.find(c => c.id === s.cropId);
        const color = crop
          ? this.fieldCropsColorPipe.transform(crop, 0.3)
          : 'rgba(119, 255, 15, 0.34)';
        const label = crop
          ? (this.serverTranslate.translate(crop, 'names') as string) ?? undefined
          : undefined;

        return {
          id: s.id as string,
          polygon: s.polygonJson as string,
          color,
          label,
        };
      });
  }

  ngAfterViewInit() {
    this.tabs = [
      {
        id: 'infos',
        label: 'General information',
        template: this.infosTemplate,
      },
      {
        id: 'contract',
        label: 'Contract',
        template: this.contractTemplate,
      },
      {
        id: 'source',
        label: 'Additional informations',
        template: this.sourceTemplate,
      },
      {
        id: 'history',
        label: 'History',
        template: this.historyTemplate,
      },
    ]
    this.cdr.detectChanges();
  }

  private loadCropYieldForecast(): void {
    if (!this.formData.id) return;

    this.cropYieldForecastService
      .aiInferenceCropYieldForecastGetByQueryPost({
        filters: [
          {
            filterField: 'seasonId',
            filterOperator: FilterOperator.Equals,
            filterValue: this.formData.id,
          },
        ],
        sortBy: 'CreateAt',
      })
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (forecasts: CropYieldForecast[]) => {
          if (forecasts && forecasts.length > 0) {
            const latestForecast = forecasts[0];

            this.formData.yieldHaPlan = latestForecast.yield ?? this.formData.yieldHaPlan;

            this.formData.yieldPlan = latestForecast.forecastAbsolute ?? this.formData.yieldPlan;
          }
        },
        error: (err) => {
          console.error('Error loading crop yield forecast:', err);
        },
      });
  }

  formatToDateOnly(dateString: string | null | undefined): string | null {
    if (!dateString) {
      return null;
    }
    const date = new Date(dateString);
    const formattedDate = this.datePipe.transform(date, 'yyyy-MM-dd');
    return formattedDate ? formattedDate : null;
  }

  private buildSeasonPayload(data: Season & {
    polygon?: string;
    rentalTermsId?: string;
    rentalStartDate?: string;
    rentalEndDate?: string;
    contourConfirmationDate?: string | null;
    cropConfirmationDate?: string | null;
    cropConfirmationSource?: string | null;
    source?: string | null;
    plannedCropId?: string | null;
    plannedVarietyId?: string | null;
    seedVarietyId?: string | null;
    sourceCropId?: string | null;
  }): Season {
    const payload = buildSeasonUpdatePayload(
      data,
      this.config?.data?.model?.polygonJson,
    );

    if (!this.isPlannedSowing && this.planFieldsSnapshot) {
      applySeasonPlanLockedSnapshot(payload as Record<string, unknown>, this.planFieldsSnapshot);
    }

    return payload;
  }

  save(): void {
    if (this.isPlannedSowing) {
      return;
    }

    this.isSaving = true;

    const transformedFormData = this.buildSeasonPayload(this.formData);

    if (!transformedFormData.id) {
      this.createFactSeason$(this.formData, transformedFormData, {
        onBeforeUpdate: (seasonId) => {
          this.formData.id = seasonId;
        },
      })
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (response) => this.updateRentalTermsRelation(response),
          error: (error) => {
            console.error('Error while saving:', error);
            this.isSaving = false;
            this.showSaveError(error);
          },
        });
    } else {
      this.seasonService
        .cropMonitoringSeasonUpdatePut(transformedFormData)
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (response) => {
            this.updateRentalTermsRelation(response);
            // this.ref.close({success: true, data: response});
          },
          error: (error) => {
            console.error('Error while saving:', error);
            this.isSaving = false;
            this.showSaveError(error);
          },
        });
    }

  }

  private showSaveError(error: unknown): void {
    const body = (error as { error?: { code?: string; message?: string } })?.error;
    if (body?.code === 'PLAN_DATA_LOCKED') {
      this.confirmationService.confirm({
        header: this.translate.instant('SEASON_PLAN_DATA_LOCKED_TITLE'),
        message: body.message ?? this.translate.instant('SEASON_PLAN_DATA_LOCKED_BANNER'),
        icon: 'pi pi-lock',
        acceptLabel: this.translate.instant('Close'),
        rejectVisible: false,
        accept: () => {},
      });
      return;
    }

    const message =
      (error as {message?: string})?.message
      ?? body?.message
      ?? this.translate.instant('Error creating season');

    this.confirmationService.confirm({
      header: this.translate.instant('Error'),
      message,
      icon: 'pi pi-times-circle',
      acceptLabel: this.translate.instant('Close'),
      rejectVisible: false,
      accept: () => {},
    });
  }

  cancel(): void {
    this.ref.close();
  }

  onHistoryRestored(): void {
    const seasonId = this.formData?.id;
    if (!seasonId) {
      return;
    }

    this.seasonService
      .cropMonitoringSeasonGetByIdIdGet(seasonId)
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (season) => {
          if (!season) {
            return;
          }
          this.formData = season;
          this.config.data.model = season;
          this.isPlannedSowing = isSeasonPlanned(season as Record<string, unknown>);
          this.planFieldsSnapshot =
            !this.isPlannedSowing && isSeasonPlanDataLocked(season as Record<string, unknown>)
              ? snapshotSeasonPlanLockedFields(season as Record<string, unknown>)
              : null;
          this.loadActiveSeason();
          if (this.formData.fieldId) {
            this.passportManager.loadField(this.formData.fieldId).subscribe();
            this.passportManager
              .loadActiveSeasons(this.formData.fieldId, this.getActiveSeasonsQueryDateParam())
              .subscribe();
          }
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Error reloading season after restore:', err),
      });
  }

  /** Resolved crop name for the current locale – used as the polygon label on the map */
  get cropName(): string | null {
    const cropId = this.isPlannedSowing ? this.formData?.plannedCropId : this.formData?.cropId;
    if (!cropId) return null;
    const crop = this.crops.find(c => c.id === cropId);
    if (!crop) return null;
    return (this.serverTranslate.translate(crop, 'names') as string) ?? null;
  }

  get cropColor() {
    const cropId = this.isPlannedSowing ? this.formData?.plannedCropId : this.formData?.cropId;
    const crop = this.crops.find(c => c.id === cropId);
    return crop ? this.fieldCropsColorPipe.transform(crop) : 'rgba(119, 255, 15, 0.34)';
  }


  private createFactSeason$(
    sourceData: Season,
    updatePayload: Season,
    options: {
      onBeforeUpdate?: (seasonId: string) => void;
      updateAfterCreate?: boolean;
    } = {},
  ): Observable<Season> {
    const updateAfterCreate = options.updateAfterCreate !== false;
    const pureFactPayload = buildCreatePureFactSeasonPayload(
      sourceData,
      sourceData.polygonJson ?? this.config?.data?.model?.polygonJson,
    );

    if (!pureFactPayload) {
      return throwError(() => new Error(this.translate.instant('SEASON_FACT_CREATE_MISSING_FIELD_OR_POLYGON')));
    }

    return this.seasonService.cropMonitoringSeasonCreatePureFactSeasonPost(pureFactPayload).pipe(
      switchMap((created: FactSeasonDto) => {
        const seasonId = created?.id;
        if (!seasonId) {
          return throwError(() => new Error('CreatePureFactSeason returned no season id'));
        }

        options.onBeforeUpdate?.(seasonId);

        if (!updateAfterCreate) {
          return of({
            ...updatePayload,
            id: seasonId,
            cropIdentificationSource:
              created.cropIdentificationSource ?? updatePayload.cropIdentificationSource,
          } as Season);
        }

        const payload = {
          ...updatePayload,
          id: seasonId,
        };
        payload.cropIdentificationSource ??= created.cropIdentificationSource;
        return this.seasonService.cropMonitoringSeasonUpdatePut(payload);
      }),
    );
  }

  private rollbackCreatedSeason$(seasonId: string): Observable<void> {
    return this.seasonService.cropMonitoringSeasonDeleteIdDelete(seasonId).pipe(
      map(() => undefined),
      catchError((deleteError) => {
        console.error('Failed to rollback season after split error:', deleteError);
        return of(undefined);
      }),
    );
  }

  private showSplitError(error: unknown): void {
    this.showSaveError(error);
  }

  afterSplit(polygons: string[]) {
    if (this.isPlannedSowing || !polygons || polygons.length !== 2) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('Do you want to create two new seasons from the split polygons?'),
      header: this.translate.instant('Confirm Split'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.processSplit(polygons);
      },
      reject: () => {},
    });
  }

  private processSplit(polygons: string[]) {
    const [polygon1, polygon2] = polygons;
    const {polygon: _parentPolygon, ...splitFormBase} = this.formData;

    this.isLoadingMap = true;
    const season1 = this.buildSeasonPayload({
      ...splitFormBase,
      cropId: null,
      id: undefined,
      polygonJson: polygon1,
      fieldArea: this.calculateAreaFromPolygon(polygon1) || null,
    });

    const season2 = this.buildSeasonPayload({
      ...splitFormBase,
      cropId: null,
      id: undefined,
      polygonJson: polygon2,
      fieldArea: this.calculateAreaFromPolygon(polygon2) || null,
    });

    this.createFactSeason$(season1, season1, {updateAfterCreate: false})
      .pipe(
        switchMap((created1) => {
          const seasonId1 = created1.id as string;
          return this.createFactSeason$(season2, season2, {updateAfterCreate: false}).pipe(
            map((created2) => ({
              seasonId1,
              seasonId2: created2.id as string,
            })),
            catchError((error) =>
              this.rollbackCreatedSeason$(seasonId1).pipe(switchMap(() => throwError(() => error))),
            ),
          );
        }),
        takeUntil(this.$destroy),
      )
      .subscribe({
        next: ({seasonId1, seasonId2}) => {
          this.ref.close({
            success: true,
            data: [{id: seasonId1}, {id: seasonId2}],
            split: true,
          });
          this.isLoadingMap = false;
        },
        error: (error) => {
          console.error('Error creating season during split:', error);
          this.isLoadingMap = false;
          this.showSplitError(error);
        },
      });
  }

  private calculateAreaFromPolygon(polygonWkt: string): number {
    const coords = PolygonConverterUtil.polygonJsonToCoordinates(polygonWkt);
    return olCalculateAreaFromPolygon(JSON.stringify(coords), ol) as any;
  }

  private loadField(): void {
    this.isLoadingMap = true;
    this.fieldService.cropMonitoringFieldGetByIdIdGet(this.formData.fieldId as string)
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (field: any) => {
          this.isLoadingMap = false;
          this.parentField = field?.entity;
          this.loadFieldRegion()
          const coordinates = extractCoordinates(this.parentField?.polygonJson as string);
          const center = PolygonConverterUtil.getPolygonCenter(coordinates);
          this.center = center ? [center.lng, center.lat] : this.center;
        },
      });
  }

  loadCrops() {
    if (this.passportManager.crops().length === 0) {
      this.passportManager.loadCrops()
        .pipe(takeUntil(this.$destroy))
      .subscribe(() => {
          this.crops = this.passportManager.crops();
          if (!this.disableSiblingPolygons && this.activeSeasons.length > 0) {
            this.activeSeasonsPolygonJson = this.prepareSiblingsPolygons();
          }
        })
    } else {
      this.crops = this.passportManager.crops();
    }
  }

  loadActiveSeason() {
    if (this.disableSiblingPolygons) {
      this.activeSeasons = [];
      this.activeSeasonsPolygonJson = [];
      return;
    }

    const fieldId = this.formData?.fieldId;
    if (!fieldId) {
      this.activeSeasons = [];
      this.activeSeasonsPolygonJson = [];
      return;
    }

    const dateParam = this.getActiveSeasonsQueryDateParam();

    this.passportManager.loadActiveSeasons(fieldId, dateParam)
      .pipe(takeUntil(this.$destroy))
      .subscribe(seasons => {
        this.activeSeasons = seasons;
        this.activeSeasonsPolygonJson = this.prepareSiblingsPolygons();
      })
  }

  loadFieldRegion() {
    this.regionManager.getRegions(this.parentField.regionId as string)
      .then(regionParent => {
        const region = regionParent.find(r => r.id === this.parentField.regionId);
        if (region?.parentId) {
          this.regionManager.getRegions(region.parentId as string)
            .then(regionDistrict => {
              this.region = regionDistrict[0]
              this.loadScheduledDates();
            })
        } else {
          this.region = regionParent[0]
          this.loadScheduledDates();
        }
      })
  }


  loadRentalTermsRelation() {
    if (!this.formData.id) {
      return;
    }
    this.rentalTermsRelation
      .cropMonitoringRentalTermsRelationGetByQueryPost({
        filters: [
          {
            filterField: 'seasonId',
            filterOperator: FilterOperator.Equals,
            filterValue: this.formData.id,
          },
        ],
      })
      .pipe(takeUntil(this.$destroy))
      .subscribe((relations) => {
        if (relations && relations.length > 0) {
          this.formData['rentalTermsId'] = relations[0].rentalTermsId;
          this.formData['rentalStartDate'] = relations[0].startDate;
          this.formData['rentalEndDate'] = relations[0].endDate;
          this.currentRentalTermRelation = relations[0];
        }
      });
  }


  sourceForm = computed<IMsfFormBuilder[]>(() => this.fieldsReadOnlyIfPlanned([
    {
      name: "cropConfirmationDate",
      title: "Crop confirmation date",
      placeholder: "Crop confirmation date",
      type: "date",
      options: {
        col: 'col-6'
      }
    },
    {
      name: 'cropConfirmationSource',
      title: 'Crop confirmation source',
      type: 'select',
      placeholder: 'Select source',
      selectOptions: IFieldSources,
      options: {
        clearable: true,
        select: {optionLabel: 'name', translateBy: 'locale'},
        col: 'col-6'
      },
    },
    {
      name: 'manualSurveyBlockReasonId',
      title: 'MANUAL_SURVEY_BLOCKERS',
      type: 'select-paginate',
      selectService: this.manualSurveyBlockReasonService,
      selectServiceMethod: 'dictionariesManualSurveyBlockReasonGetByQueryPost',
      placeholder: 'MANUAL_SURVEY_BLOCKERS',
      options: {
        clearable: true,
        col: 'col-6',
      },
    },
    {
      name: 'remoteSurveyBlockReasonId',
      selectService: this.remoteSurveyBlockReasonService,
      selectServiceMethod: 'dictionariesRemoteSurveyBlockReasonGetByQueryPost',
      title: 'REMOTE_SURVEY_BLOCKERS',
      type: 'select-paginate',
      placeholder: 'REMOTE_SURVEY_BLOCKERS',
      options: {
        clearable: true,
        col: 'col-6',
      },
    },
    {
      name: 'monitoring',
      title: 'IS_MONITORING',
      type: 'checkbox',
      options: {
        col: 'col-12',
      },
    },
  ]));

  contractForm = computed<IMsfFormBuilder[]>(() => this.fieldsReadOnlyIfPlanned([
    {
      name: 'governmentContractStartDate',
      title: 'Government contract start date',
      type: 'date',
      placeholder: 'Start Date',
      options: {
        col: 'col-4',
      },
    },
    {
      name: 'governmentContractEndDate',
      title: 'Government contract end date',
      type: 'date',
      placeholder: 'End Date',
      options: {
        col: 'col-4',
      },
    },
    {
      name: 'governmentContractNumber',
      title: 'Government contract number',
      type: 'input',
      placeholder: 'Government contract number',
      options: {
        col: 'col-4',
      },
    },

    {
      name: 'rentalStartDate',
      title: 'Rental start date',
      type: 'date',
      placeholder: 'Start Date',
      options: {
        col: 'col-4',
      },
    },
    {
      name: 'rentalEndDate',
      title: 'Rental end date',
      type: 'date',
      placeholder: 'End Date',
      options: {
        col: 'col-4',
      },
    },
    {
      name: 'rentalTermsId',
      title: 'Rental terms condition',
      type: 'select',
      selectService: this.rentalTermsService,
      selectServiceMethod: 'dictionariesRentalTermsGetByQueryPost',
      placeholder: 'Rental terms condition',
      options: {
        clearable: true,
        col: 'col-4',
      },
    },
  ]));

  detailsFields = computed<IMsfFormBuilder[]>(() => this.fieldsReadOnlyIfPlanned(this.decorateInfoFields([
    {
      name: 'seedVarietyId',
      title: 'Seed generation',
      type: 'select-paginate',
      placeholder: 'Select Seed generation',
      selectService:this.seedVarietyService,
      selectServiceMethod: 'dictionariesSeedVarietyGetByQueryPost',
      options: {
        clearable: true,
        col: 'col-4',
      },
    },
    {
      name: 'plantingMethodId',
      title: 'Planting Method',
      type: 'select-paginate',
      selectService: this.plantingMethodService,
      selectServiceMethod: 'dictionariesPlantingMethodGetByQueryPost',
      placeholder: 'Select planting method',
      options: {
        clearable: true,
        col: 'col-4',
      },
    },
    {
      name: 'fieldArea',
      title: 'Area Ga',
      type: 'input-group',
      placeholder: 'Enter',
      options: {
        inputGroup: {
          iconRight: 'pi pi-calculator',
          actionRight: () => {
          },
        },
        col: 'col-4',
      },
    },
  ])));

  commentFields = computed<IMsfFormBuilder[]>(() => this.fieldsReadOnlyIfPlanned(this.decorateInfoFields([
    {
      name: 'comment',
      title: 'Comment',
      type: 'textarea',
      placeholder: 'Enter comment',
    },
  ])));

  seasonPeriodHistoryFields = computed<IMsfFormBuilder[]>(() => [
    {
      name: 'plantingDate',
      title: 'Actual sowing date',
      type: 'date',
      placeholder: 'Actual sowing date',
      options: {
        col: 'col-6',
        date: {dateOnly: true},
      },
    },
    {
      name: 'harvestingDate',
      title: 'Actual collection date',
      type: 'date',
      placeholder: 'Actual collection date',
      options: {
        col: 'col-6',
        date: {dateOnly: true},
      },
    },
  ]);

  get infosFormFields(): IMsfFormBuilder[] {
    return this.fieldsReadOnlyIfPlanned(this.config.data?.fields ?? []);
  }

  get historyFormFields(): IMsfFormBuilder[] {
    return [
      ...this.infosFormFields,
      ...this.seasonPeriodHistoryFields(),
      ...this.detailsFields(),
      ...this.contractForm(),
      ...this.sourceForm(),
      ...this.commentFields(),
    ];
  }

  get planDataSourceLabel(): string {
    const key = resolvePlanDataSourceLabelKey(this.formData?.planDataSource);
    if (key) {
      return this.translate.instant(key);
    }
    const raw = this.formData?.planDataSource;
    if (raw === null || raw === undefined || raw === '') {
      return '—';
    }
    return String(raw);
  }

  private fieldsReadOnlyIfPlanned(fields: IMsfFormBuilder[]): IMsfFormBuilder[] {
    if (!this.isPlannedSowing) {
      return fields;
    }
    return fields.map((field) => ({...field, disabled: () => true}));
  }

  private decorateInfoFields(fields: IMsfFormBuilder[]): IMsfFormBuilder[] {
    return fields.map((field: any) => {
      if (field.name === 'fieldArea') {
        field.options.inputGroup.actionRight = () => {
          this.confirmationService.confirm({
            message: this.translate.instant('Do you want to get the field area from the polygon ?'),
            header: this.translate.instant('Confirm Action'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
              this.formData.fieldArea =
                this.calculateAreaFromPolygon(this.formData.polygon || this.config?.data?.model?.polygonJson) || null;
            },
          });
        };
      }
      return field;
    });
  }

  formChange() {
    const periodKey = this.getActiveSeasonsPeriodKey();
    if (periodKey !== this.lastActiveSeasonsPeriodKey) {
      this.lastActiveSeasonsPeriodKey = periodKey;
      this.loadActiveSeason();
    }

    if (!this.isPlannedSowing) {
      this.validateMainFormDates();
    }
  }

  onMapCanSaveChange(value: boolean): void {
    this.mapCanSave = value;
    this.updateCanSaveState();
  }

  onSeasonPeriodCanSaveChange(value: boolean): void {
    this.seasonPeriodCanSave = value;
    this.updateCanSaveState();
  }

  validateMainFormDates(): void {
    const datePairs = [
      {
        start: 'governmentContractStartDate',
        end: 'governmentContractEndDate',
        msg: this.translate.instant('Contract end date cannot be earlier than start date'),
      },
      {
        start: 'rentalStartDate',
        end: 'rentalEndDate',
        msg: this.translate.instant('Rental end date cannot be earlier than start date'),
      },
    ];
    this.mainFormDateError = null;
    for (const pair of datePairs) {
      const start = this.formData?.[pair.start];
      const end = this.formData?.[pair.end];
      if (start && end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (endDate < startDate) {
          this.mainFormDateError = pair.msg;
          this.mainFormCanSave = false;
          this.updateCanSaveState();
          return;
        }
      }
    }
    this.mainFormCanSave = true;
    this.updateCanSaveState();
    const cropChanged: string = this.formData && this.formData.cropId !== this.lastCropId;

    if (cropChanged) {
      this.lastCropId = this.formData.cropId;
      this.loadScheduledDates();
      return;
    }
  }

  private updateCanSaveState(): void {
    this.canSave = this.mainFormCanSave && this.seasonPeriodCanSave && this.mapCanSave;
  }

  updateRentalTermsRelation(response:Season) {
    this.isSaving = true;

    const relationData: RentalTermsRelation = {
      seasonId: this.formData.id,
      rentalTermsId: this.formData.rentalTermsId,
      startDate: this.formatToDateOnly(this.formData.rentalStartDate) as string,
      endDate: this.formatToDateOnly(this.formData.rentalEndDate) as string,
      area: this.formData.area,
      fieldId: this.formData.fieldId,
      polygonJson: this.formData.polygon || this.config?.data?.model?.polygonJson,
    };

    if (!relationData.startDate || !relationData.endDate) {
      this.ref.close({success: true, data: response});
      this.isSaving = false;
      return;
    }

    if (this.currentRentalTermRelation) {
      relationData.id = this.currentRentalTermRelation.id;
      this.rentalTermsRelation
        .cropMonitoringRentalTermsRelationUpdatePut(relationData)
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (response) => {
            this.ref.close({success: true, data: response});
          },
          error: (error) => console.error('Error updating rental terms relation:', error),
          complete: () => {
            this.isSaving = false;
          },
        });
    } else {
      this.rentalTermsRelation
        .cropMonitoringRentalTermsRelationCreatePost(relationData)
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (response) => {
            this.ref.close({success: true, data: response});
            console.log('Rental terms relation created:', response);
          },
          error: (error) => console.error('Error creating rental terms relation:', error),
          complete: () => {
            this.isSaving = false;
          },
        });
    }
  }

  loadScheduledDates(): void {
    if (this.isPlannedSowing) {
      return;
    }
    if (!this?.formData?.cropId || !this.region?.id) {
      return;
    }

    this.recommendedPlantingDatesService.dictionariesRecommendedPlantingDatesGetAllCustomGet(
      new Date().getFullYear(),
      this.formData.cropId,
      this.region.id as string
    )
      .pipe(takeUntil(this.$destroy))
      .subscribe((recommendedPlantingDates) => {
        if (recommendedPlantingDates.length > 0 &&
          recommendedPlantingDates[0].periods &&
          recommendedPlantingDates[0].periods.length > 0 &&
          recommendedPlantingDates[0].periods[0].harvestingStartDate) {

          this.formData.harvestingDatePlan = recommendedPlantingDates[0].periods[0].harvestingStartDate;
          this.formData.plantingDatePlan = recommendedPlantingDates[0].periods[0].seedingStartDate;

        }
      });
  }

  /**
   * Active siblings should be loaded for the currently edited season period,
   * otherwise the map siblings default to server "today" and may not match what you see in tables/history.
   */
  private getActiveSeasonsQueryDateParam(): string | undefined {
    const raw = this.isPlannedSowing
      ? this.formData?.plantingDatePlan
      : this.formData?.plantingDate;

    if (!raw) {
      return undefined;
    }

    // Backend expects `DateOnly` (`yyyy-MM-dd`), so we must not introduce UTC day shifts.
    // Use local Y-M-D for Date objects; for ISO strings, keep the existing date part.
    if (typeof raw === 'string') {
      // Common cases: `yyyy-MM-dd` or ISO with time part.
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return raw;
      }
      if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
        return raw.slice(0, 10);
      }

      // Also support UI formats like `dd/MM/yyyy` or `dd.MM.yyyy` (the table displays dd/MM/yyyy).
      const m = raw.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
      if (m) {
        const dd = m[1];
        const mm = m[2];
        const yyyy = m[3];
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const d = raw instanceof Date ? raw : new Date(raw as string);
    if (Number.isNaN(d.getTime())) {
      return undefined;
    }

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private getActiveSeasonsPeriodKey(): string {
    const planting = this.isPlannedSowing
      ? this.formData?.plantingDatePlan
      : this.formData?.plantingDate;
    const harvesting = this.isPlannedSowing
      ? this.formData?.harvestingDatePlan
      : this.formData?.harvestingDate;

    // Include both sides: HarvestingDate affects "active" status on the backend.
    return `${planting ?? ''}|${harvesting ?? ''}`;
  }


  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }


}
